import { useEffect, useRef, useState } from "react";
import Card from "../../../elements/Card";
import Select from "../../../form/select/Select";
import { useParams } from "react-router-dom";
import useClickOutside from "../../../../hooks/useClickOutside";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { categoryList } from "../../../../services/categoryService";
import { lngDetect } from "../../../../helpers/lngDetect";
import { organizationList } from "../../../../services/organizationService";
import { toast } from "react-toastify";
import Tags from "../../../form/Tags";
import Radio from "../../../form/Radio";
import Input from "../../../form/Input";
import MultiselectDropdown from "../../../form/MultiselectDropdown";
import { collectionList } from "../../../../services/collectionService";
import { useOrganization } from "../../../../context/OrganizationContext";
import { t } from "i18next";

const validation = {
  type: { required: true, message: "type is required" },
  category: { required: true, message: "category is required" },
  organization: { required: true, message: "organization is required" },
  daysBeforeRefund: {
    type: "number",
    errorType: "info",
    max: 30,
    min: 1,
    message: "Value should be set (1-30)",
  },
};

const ReadOnly = ({ data }) => {
  return (
    <Card bodyClass={"space-y-6"}>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 space-y-4">
          <div className="space-y-2">
            <h3 className="font-semibold text-lg">{t("Event Collections")}</h3>
            <div className="text-text-gradient-red font-medium">
              {data?.collectionData && data.collectionData.length > 0 ? (
                <div className="space-y-2">
                  <div className="font-semibold">
                    {data.collectionData.length} {t("collection")}
                    {data.collectionData.length > 1 ? "s" : ""} {t("selected")}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {data.collectionData.map((collection, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 border border-blue-200"
                      >
                        {lngDetect(collection.name) || collection}
                      </span>
                    ))}
                  </div>
                  <div className="text-xs text-slate-500">
                    {t("This event is part of the selected collections")}
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="font-semibold">{t("No Collections")}</div>
                  <div className="text-xs text-slate-500">
                    {t("This event is not part of any collections")}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const SearchSetting = ({
  values = {},
  handleChange = () => {},
  formValidate = () => {},
  setFieldsValue = () => {},
  errors,
}) => {
  const divRef = useRef(null);
  const { eventId } = useParams();
  const [readOnly, setReadOnly] = useState(false);
  const { selectedOrganization } = useOrganization();
  const [collectionFilters, setcollectionFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
    organization: selectedOrganization,
  });

  useClickOutside(divRef, () => {
    if (eventId) {
      setReadOnly(true);
    } else {
      setReadOnly(false);
    }
  });

  const {
    data: collectionData,
    isLoading: collectionLoading,
    isFetching: collectionFetching,
  } = useQuery({
    queryKey: ["Collection", collectionFilters],
    queryFn: async () => {
      const res = await collectionList({
        ...collectionFilters,
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  useEffect(() => {
    if (eventId) {
      setReadOnly(true);
    }
  }, [eventId]);

  return (
    <div
      ref={divRef}
      onClick={() => {
        if (eventId) {
          setReadOnly(false);
        }
      }}
    >
      {readOnly ? (
        <ReadOnly data={values} />
      ) : (
        <Card bodyClass={"space-y-6"}>
          <div className="relative mb-5">
            <MultiselectDropdown
              label={t("Add your event to a collection")}
              labelClasses={"text-lg font-semibold text-black mb-1"}
              type="tag"
              dropdownData={collectionData?.data?.map((item) => ({
                value: item?._id,
                name: lngDetect(item?.name),
              }))}
              values={values?.collections}
              onChange={(val) => {
                setFieldsValue({
                  collections: val,
                  collectionData: collectionData?.data?.filter((item) =>
                    val?.includes(item?._id)
                  ),
                });
              }}
              buttonPlaceholder={t("Select collections")}
              dropdownButtonClass="border-gray-300 justify-start h-12"
              buttonArrowIcon="fa-solid fa-chevron-down"
              xPlacement="bottom-start"
              hasLogo={false}
              {...formValidate(errors, "collections")}
            />
          </div>
        </Card>
      )}
    </div>
  );
};

export default SearchSetting;
